import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTRadio, {
  KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import ImageView from 'general/components/quybka/Images/ImageView';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

MaintainerCreateContractView.propTypes = {
  title: PropTypes.string,
  contentEl: PropTypes.element,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  contract: PropTypes.object,
  agency: PropTypes.object,
  contractRole: PropTypes.string,
};

function MaintainerCreateContractView({
  title = '',
  contentEl = null,
  onCancel,
  onConfirm,
  contract,
  agency,
  contractRole,
}) {
  // MARK: ---- PROPERTIES ----
  const [loading, setLoading] = useState(false);
  const [contractDate, setContractDate] = useState(moment().format('YYYY-MM-DD'));
  const [contractNumber, setContractNumber] = useState('');
  const [validFrom, setValidFrom] = useState(moment().format('YYYY-MM-DD'));
  const [validTo, setValidTo] = useState(moment().add(1, 'year').format('YYYY-MM-DD'));
  const [selectedFiles, setSelectedFiles] = useState([]);
  const selectFilePathList = useRef([]);
  const [allFiles, setAllFiles] = useState([]);
  const [contractType, setContractType] = useState(AgencyConstants.ContractType.DIGITAL);
  const [paperContractDocumentFile, setPaperContractDocumentFile] = useState(null);
  useEffect(() => {
    if (Utils.isValid(contract)) {
      setContractDate(Utils.formatDateTimeToString(contract?.contractDate, 'YYYY-MM-DD'));
      setContractNumber(contract?.contractNumber);
      setContractType(contract?.contractType);
      setValidFrom(Utils.formatDateTimeToString(contract?.validFrom, 'YYYY-MM-DD'));
      setValidTo(Utils.formatDateTimeToString(contract?.validTo, 'YYYY-MM-DD'));
      setPaperContractDocumentFile(contract?.contractDocument);
      // setAllFiles(contract?.attachedFiles);
    }
  }, [contract]);

  useEffect(() => {
    if (contractRole === AgencyConstants.ContractRole.FRANCHISE) {
      setContractType(AgencyConstants.ContractType.PAPER);
    }
  }, [contractRole])

  useEffect(() => {
    const getContractNumber = async (orderType) => {
      const contractNumber = await MaintainerHelper.API.requestGenerateContractNumber({ orderType });
      if (contractNumber) {
        setContractNumber(contractNumber);
      }
    };
    if (!Utils.isValid(contract)) {
      getContractNumber(contractRole ?? 'SALE');
    }
  }, [contract, contractRole]);

  // MARK: ---- FUNCTIONS ----
  const handleFilesChanged = (e) => {
    const files = e.target.files;
    const tmpSelectedFiles = [...selectedFiles];
    tmpSelectedFiles.push(...files);
    setSelectedFiles(tmpSelectedFiles);
    setAllFiles([...allFiles, ...tmpSelectedFiles]);
  };
  const handleRemoveAll = () => {
    setSelectedFiles([]);
  };
  const handleViewFile = (file) => {
    const url = URL.createObjectURL(file);
    if (url) {
      Utils.openInNewTab(url);
      URL.revokeObjectURL(url);
    }
  };
  const handleRemoveFile = (file) => {
    if (file instanceof File) {
      const tmpFiles = selectedFiles.filter((item) => item.lastModified !== file?.lastModified);
      setSelectedFiles(tmpFiles);
    }

    let newAllFiles = [...allFiles];
    if (typeof file === 'string') {
      newAllFiles = _.filter(allFiles, (item) => item !== file);
    }
    if (file instanceof File) {
      newAllFiles = _.filter(allFiles, (item) => item.lastModified !== file?.lastModified);
    }
    setAllFiles(newAllFiles);
  };
  async function processSelectedFiles(files) {
    for (const file of files) {
      const path = await uploadAsyncFunction(file);
      selectFilePathList.current?.push(path);
    }
  }
  async function uploadAsyncFunction(file) {
    return new Promise((resolve) => {
      if (file instanceof File) {
        miscHelper.callApiUploadFile(file).then((mediaList) => {
          if (_.isArray(mediaList) && mediaList.length > 0) {
            const firstMedia = _.first(mediaList);
            const relativePath = firstMedia?.relativePath;
            if (relativePath) {
              resolve(relativePath);
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        });
      } else {
        resolve(file);
      }
    });
  }

  const handleConfirm = async () => {
    if (!contractDate || !contractNumber || !validFrom || !validTo) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    if (contractType === AgencyConstants.ContractType.PAPER && !paperContractDocumentFile) {
      ToastHelper.showError('Vui lòng tải lên hợp đồng scan');
      return;
    }

    if (!_.isEmpty(selectedFiles)) {
      selectFilePathList.current = [];
      await processSelectedFiles(selectedFiles);
    }
    setAllFiles([
      ..._.filter(allFiles, (item) => typeof item === 'string'),
      ...selectFilePathList.current,
    ]);
    const attachedFiles = [
      ..._.filter(allFiles, (item) => typeof item === 'string'),
      ...selectFilePathList.current,
    ];

    const param = {
      contractType: contractType,
      contractDate,
      contractNumber,
      validFrom,
      validTo,
      attachedFiles,
      contractDocument: contract?.contractDocument ?? '',
    };
    setLoading(true);
    if (contractType === AgencyConstants.ContractType.PAPER) {
      if (paperContractDocumentFile) {
        param.contractDocument = await uploadAsyncFunction(paperContractDocumentFile);
      }
      // gan luon trang thai la hoan thanh
      param.status = AgencyConstants.ContractStatus.COMPLETED;
    }
    setLoading(false);
    onConfirm(param);
  };

  return (
    <div className="">
      <div className="d-flex flex-column align-items-center justify-content-center text-center p-5">
        <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>{title}</span>
        <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>{contentEl}</span>
      </div>

      {/* chon loai hop dong */}
      <div
        style={{
          borderBottom: `1px dashed ${AppColors.gray[200]}`,
        }}
      >
        <div className="radio-inline w-100 px-5 mb-3">
          <div className="d-flex align-items-center gap-5">
            <KTRadio
              name="typeContractElectronic"
              label="Hợp đồng điện tử"
              type={KTRadioType.outline}
              additionalClassName="radio-success"
              onChange={(value) => {
                setContractType(AgencyConstants.ContractType.DIGITAL);
              }}
              checked={contractType === AgencyConstants.ContractType.DIGITAL}
              disabled={Utils.isValid(contract) || contractRole === AgencyConstants.ContractRole.FRANCHISE}
            />
            <KTRadio
              name="typeContractPaper"
              label="Hợp đồng giấy"
              type={KTRadioType.outline}
              additionalClassName="radio-success"
              onChange={(value) => {
                setContractType(AgencyConstants.ContractType.PAPER);
              }}
              checked={contractType === AgencyConstants.ContractType.PAPER}
              disabled={Utils.isValid(contract)}
            />
          </div>
        </div>
      </div>

      <div className="pb-2 d-flex flex-column gap-3 mt-4">
        {/* ngay hop dong */}
        <div className="px-5">
          {UIHelper.buildFormLabel('Ngày hợp đồng', true)}
          <KTFormInput
            name="contractDate"
            placeholder="Nhập ngày hợp đồng"
            type={KTFormInputType.date}
            value={contractDate}
            onChange={(value) => setContractDate(value)}
          />
        </div>
        {/* so hop dong */}
        <div className="px-5">
          {UIHelper.buildFormLabel('Số hợp đồng', true)}
          <KTFormInput
            name="contractNumber"
            placeholder="Nhập số hợp đồng"
            type={KTFormInputType.text}
            value={contractNumber}
            onChange={(value) => setContractNumber(value)}
            disabled={!!contract}
          />
        </div>
        {/* ngay hieu luc va het hieu luc */}
        <div className="row mx-0 mb-0 p-0 px-5">
          <div className="col-6 pl-0 pr-2">
            {UIHelper.buildFormLabel('Ngày hiệu lực', true)}
            <KTFormInput
              name="contractValidFrom"
              placeholder="Nhập ngày hiệu lực"
              type={KTFormInputType.date}
              value={validFrom}
              onChange={(value) => setValidFrom(value)}
              minTime={contractDate}
            />
          </div>
          <div className="col-6 pr-0 pl-2">
            {UIHelper.buildFormLabel('Ngày hết hiệu lực', true)}
            <KTFormInput
              name="contractValidTo"
              placeholder="Nhập ngày hết hiệu lực"
              type={KTFormInputType.date}
              value={validTo}
              onChange={(value) => setValidTo(value)}
              minTime={validFrom}
            />
          </div>
        </div>
        {/* file dinh kem */}
        {contractType === AgencyConstants.ContractType.DIGITAL && (
          <div className="">
            <div className="px-5 d-flex align-items-center gap-1">
              {contractType === AgencyConstants.ContractType.DIGITAL
                ? UIHelper.buildFormLabel('Tải lên tệp tin đính kèm', false, false)
                : UIHelper.buildFormLabel('Tải lên hợp đồng scan và giấy uỷ quyền', true)}
              {contractType === AgencyConstants.ContractType.DIGITAL && (
                <KTTooltip text="Tệp đính kèm tải lên sẽ không ảnh hưởng đến nội dung hợp đồng điện tử.">
                  <i className="fa-regular fa-circle-info" style={{ color: AppColors.info }} />
                </KTTooltip>
              )}
            </div>
            <div>
              {/* danh sach file dang duoc chon */}
              {!_.isEmpty(allFiles) && (
                <div className="row m-0 p-0 px-3">
                  {allFiles.map((item, index) => {
                    return (
                      <div className="p-0 col-6 mb-2 p-2" key={item?.lastModified ?? item ?? index}>
                        <ImageView
                          name={item?.lastModified ?? item ?? index}
                          path={_.isString(item) ? item : item?.name}
                          onRemove={() => {
                            handleRemoveFile(item);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="d-flex px-5">
                <label className="">
                  {UIHelper.buildNegativeButton(
                    'Thêm tập tin',
                    () => { },
                    <i className="fa-regular fa-upload" />
                  )}
                  <input
                    type="file"
                    accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                    onChange={handleFilesChanged}
                    multiple
                    hidden
                    onClick={(e) => {
                      e.target.value = null;
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* file hop dong & file giay uy quyen truong hop hop dong giay (PAPER) */}
        {contractType === AgencyConstants.ContractType.PAPER && (
          <div>
            <div className="row m-0 p-0 px-3">
              {/* file hop dong */}
              <div className="col-6 p-0 px-2">
                <div>
                  {UIHelper.buildFormLabel('Tải lên hợp đồng', true)}
                  <label className="">
                    {UIHelper.buildNegativeButton(
                      'Thêm tập tin',
                      () => { },
                      <i className="fa-regular fa-upload" />
                    )}
                    <input
                      type="file"
                      accept={['.pdf']}
                      onChange={(e) => {
                        const files = e.target.files;
                        if (!files || files.length === 0) return;
                        const file = files[0];
                        setPaperContractDocumentFile(file);
                      }}
                      hidden
                      onClick={(e) => {
                        e.target.value = null;
                      }}
                    />
                  </label>
                  {paperContractDocumentFile && (
                    <div>
                      <ImageView
                        name={paperContractDocumentFile?.lastModified ?? paperContractDocumentFile}
                        path={
                          _.isString(paperContractDocumentFile)
                            ? paperContractDocumentFile.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                            : paperContractDocumentFile?.name
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* footer */}
      <div
        className="d-flex align-items-center gap-3 px-4 py-3"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null, true)}
        {UIHelper.buildGreenButton(
          !Utils.isValid(contract) ? 'Tạo hợp đồng' : 'Cập nhật',
          handleConfirm,
          null,
          true,
          loading
        )}
      </div>
    </div>
  );
}

export default MaintainerCreateContractView;
